// 單位主管推薦整頁（vote-dept-manager-recommend＝legacy vote/deptManagerRecommend.jsp）。
// 批次 21（Gitea issue #24，B8-3 未涵蓋）。掛在 PageBehavior.complexBody（批次 16/17 crud-list
// 頁 hook 先例）。
//
// legacy 流程（行號親驗 2026-09-27；基準＝views/vote/deptManagerRecommend.jsp＋modal/recommendTeacher.jsp
// ＋js-my/vote/deptManagerRecommend.js(351)＋deptManagerRecommendUtils.js.gz(35，CheckMarx 藏檔)；
// 詳 docs/plans/2026-09-27-batch21-vote-pages-plan.md §1.1）：
//   - ready（js:18-23）＝initQueryForm＋bindEvents＋initDataTable＋**query()（ready 即查詢）**。
//   - 常數（js:10/:15）＝annualDisp 0（民國年）、maxVote 2（主治2票、住院2票）。
//   - 查詢（js:250-275 getVoteQueryData→GET vote/list :277-303）：{orgId, deptManagerId,
//     findExcellentTeacherRecommended:true, annualId?(>0), startDate?, endDate+' 23:59:59'?,
//     enableTypes:['ENABLE']}——mock 層以契約 DOM name 直送 filters（batch 17 同款，D21-4）。
//   - 表 4 欄（jsp:112-115＋js:305-338 initDataTable）：年度（annualYear 民國直出）/投票名稱/
//     投票期間/投票結果（!excellentTeacherRecommended→投票 btn.vote :325-326；檢視 btn.viewLog 恆有 :328）。
//   - 投票鈕（js:32-48）：getRecommendTaecherList({voteId,isVote:true}→GET vote/getRecommendTeacher，
//     utils.gz:12-34)→list>0→renderTeacherTable(list,false)＋btn-submit show＋modal show；
//     else showAlertDialog('本次投票活動沒有貴科部醫師名單，請洽教學室承辦人',null,'提示')(:44)。
//   - 檢視鈕（js:51-67）：isVote:false→readonly＋btn-submit hide(:56-60)；空清單同 alert(:63)。
//   - 推薦 toggle（js:73-94）：voteCnt＝同表 voted 數；<2→voted 互换；else '已達[type]推薦上限！'(:86)
//     （type＝teacherType 文字＝主治醫師/住院醫師；teacherRowTmpl voted="false" 初始＝:99）。
//   - 送出（js:112-165）：getRecommendTeacherData(:168-198)＝attendingPhysicians/residents
//     [{excellentTeacherId,deptManagerRecommend}]+attending/resident 計數；>2→'主治醫師推薦名單超過2名！'
//     (:116)／'住院醫師推薦名單超過2名！'(:121)（UI 上限已擋＝防禦性分支）；confirm title
//     『是否確定要送出？』content『確定送出後，投票結果將不能修改』確定/取消(:126-133)→POST
//     vote/recommendTeacher/submit(:136-152)→success showInfoDialog(resp.desc)＋hide＋query()；
//     error alert '推薦失敗，請聯絡系統管理員！'(:151)。server desc＝推薦成功（VoteController.java:443）。
//   - resetTime（js:99-105）清投票期間（D6：legacy 圓鈕以文字鈕呈現，批次 17 同款）。
//
// 雛形策略：推薦名單＝樣本常數（vote/getRecommendTeacher endpoint-map 未映射→0 data-effect，
// batch 17 TEACHER_LIST_SAMPLE 先例，D21-4）；送出＝dataSource.update(vote row
// excellentTeacherRecommended)（dataEffect update:<pageId>；POST /vote/recommendTeacher/submit
// 未映射＝parity 登記）；送出後 re-query＝投票鈕消失（excellentTeacherRecommended=1，js:143 query()）。
// confirm 的 legacy title/content 兩段以單訊息合併呈現（DialogsApi.confirm 無 title 參數，D21-10）。
//
// 【證據等級】§7 第 3 級（原始碼）。種子【雛形值待實機校準】。

import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { Button } from '@/components/base/Button';
import { Select } from '@/components/base/Select';
import { Table, type TableColumn, type TableRow } from '@/components/base/Table';
import { TextInput } from '@/components/base/TextInput';
import { ContentPanel, ModalFrame, ProcessingOverlay, useDialogs } from '@/components/layouts';
import { LegacyContractModals } from '@/components/layouts/LegacyContractModals';
import {
  ACCOUNT_ORGANIZATION_OPTIONS,
  COURSE_CLAZZ_ANNUAL_OPTIONS,
} from '@/mocks/resource-rules';
import { selectOptionsOverride } from '@/mocks/legacy-fields';
import { MockDataError, useDataSource } from '@/mocks';
import { kebab } from '../main/dashboard/dashboardParts';

/** 年度下拉（js:45-53：全部 -1＋annualList；值＝民國年，批次 17 VOTE_ANNUAL_QUERY_OPTIONS 同源）。 */
const VOTE_ANNUAL_OPTIONS = [{ value: '-1', label: '全部' }, ...COURSE_CLAZZ_ANNUAL_OPTIONS];

/** 訊息字面值（deptManagerRecommend.js／VoteController.java；!＝FF01、?＝003F、！＝FF01）。 */
const TEXT_NO_TEACHER_LIST = '本次投票活動沒有貴科部醫師名單，請洽教學室承辦人'; // js:44/:63（title 提示）
const TEXT_RECOMMEND_LIMIT = (type: string) => `已達[${type}]推薦上限！`; // js:86
const TEXT_ATTENDING_OVER = '主治醫師推薦名單超過2名！'; // js:116
const TEXT_RESIDENT_OVER = '住院醫師推薦名單超過2名！'; // js:121
const TEXT_SUBMIT_CONFIRM = '是否確定要送出？'; // js:128（title；content :130 併入訊息，D21-x）
const TEXT_SUBMIT_CONFIRM_CONTENT = '確定送出後，投票結果將不能修改'; // js:130
const TEXT_SUBMIT_SUCCESS = '推薦成功'; // VoteController.java:443（resp.desc 鏡像）
const TEXT_SUBMIT_ERROR = '推薦失敗，請聯絡系統管理員！'; // js:151
const TEXT_QUERY_ERROR = '查詢失敗，請聯絡系統管理員！'; // js:297 error handler
const MAX_VOTE = 2; // js:15（主治2票、住院2票）

/** 推薦教師列（getRecommendTeacher 回應；teacherRowTmpl recommendTeacher.jsp:86-105＋
 *  renderTeacherTable js:200-218 的 category.id 1＝主治（#attendingPhysicianTable）、2＝住院）。 */
interface RecommendTeacherRow {
  readonly id: string;
  readonly deptName: string;
  readonly accountName: string;
  readonly studentVotePercentage: string;
  readonly recommend: number;
  readonly category: { readonly id: number };
}

/**
 * 推薦名單樣本常數（vote/getRecommendTeacher endpoint-map 未映射→0 data-effect；
 * batch 17 檢視 modal 樣本常數先例）。V003＝空清單（js:38-47 alert 分支樣本）。
 * 【雛形值待實機校準】
 */
export const RECOMMEND_TEACHER_SAMPLE: Readonly<Record<string, readonly RecommendTeacherRow[]>> = {
  V003: [],
  default: [
    { id: 'T001', deptName: '內科部', accountName: '教師甲', studentVotePercentage: '90%', recommend: 0, category: { id: 1 } },
    { id: 'T002', deptName: '外科部', accountName: '教師乙', studentVotePercentage: '85%', recommend: 0, category: { id: 1 } },
    { id: 'T003', deptName: '急診醫學部', accountName: '醫師丙', studentVotePercentage: '92%', recommend: 0, category: { id: 2 } },
    { id: 'T004', deptName: '神經內科部', accountName: '醫師丁', studentVotePercentage: '88%', recommend: 0, category: { id: 2 } },
    { id: 'T005', deptName: '復健醫學部', accountName: '醫師戊', studentVotePercentage: '83%', recommend: 0, category: { id: 2 } },
  ],
};

const MODAL_TABLE_HEAD: readonly string[] = ['科部', '姓名', '學生推薦指數90%', '主管推薦指數10%'];

/** 推薦 modal（recommendTeacher.jsp:107-158 #recommendModal 單位主管推薦；modal-title :115）。 */
function RecommendModal(props: {
  readonly pageId: string;
  readonly open: boolean;
  readonly voteName: string;
  readonly list: readonly RecommendTeacherRow[];
  /** true＝檢視（btn-submit hide，js:56-60）；false＝投票（可勾選）。 */
  readonly readonlyMode: boolean;
  readonly onClose: () => void;
  /** 送出（js:112-165）：帶勾選結果（getRecommendTeacherData :168-198 直讀 DOM 的等價）。 */
  readonly onSubmit: (votedAttending: number, votedResident: number) => void;
}): ReactElement {
  const dialogs = useDialogs();
  const { pageId, readonlyMode } = props;
  // 勾選狀態（js:73-94：voted attr；非 readonly 模式初始全 false＝teacherRowTmpl voted="false" :99）。
  const [votedMap, setVotedMap] = useState<Readonly<Record<string, boolean>>>({});

  const attending = props.list.filter((teacher) => teacher.category.id === 1);
  const residents = props.list.filter((teacher) => teacher.category.id === 2);

  /** 推薦 toggle（js:73-94）：voteCnt＝同表 voted=true 數；<maxVote→切換；else alert(:86)。 */
  const toggle = (section: 'attending' | 'resident', type: string, teacherId: string): void => {
    const pool = section === 'attending' ? attending : residents;
    const voteCnt = pool.filter((teacher) => votedMap[teacher.id] === true).length;
    if (votedMap[teacherId] !== true) {
      if (voteCnt < MAX_VOTE) {
        setVotedMap((current) => ({ ...current, [teacherId]: true }));
      } else {
        void dialogs.alert(TEXT_RECOMMEND_LIMIT(type));
      }
    } else {
      setVotedMap((current) => ({ ...current, [teacherId]: false }));
    }
  };

  /**
   * 送出驗證鏉（js:112-133）：計數（getRecommendTeacherData :168-198 的 attendingPhysicianVoteCnt/
   * residentVoteCnt）→>2 alert（:116/:121，UI 上限已擋＝防禦性分支）→confirm（:126-133）。
   */
  const submit = async (): Promise<void> => {
    const attendingVoteCnt = attending.filter((teacher) => votedMap[teacher.id] === true).length;
    const residentVoteCnt = residents.filter((teacher) => votedMap[teacher.id] === true).length;
    if (attendingVoteCnt > MAX_VOTE) {
      void dialogs.alert(TEXT_ATTENDING_OVER);
      return;
    }
    if (residentVoteCnt > MAX_VOTE) {
      void dialogs.alert(TEXT_RESIDENT_OVER);
      return;
    }
    const confirmed = await dialogs.confirm(`${TEXT_SUBMIT_CONFIRM}\n${TEXT_SUBMIT_CONFIRM_CONTENT}`);
    if (!confirmed) return;
    props.onSubmit(attendingVoteCnt, residentVoteCnt);
  };

  /** 推薦教師資料列（teacherRowTmpl；主管推薦指數欄＝勾選圓鈕/歷史圓點）。 */
  const renderRows = (section: 'attending' | 'resident', type: string, rows: readonly RecommendTeacherRow[]) =>
    rows.map((teacher) => {
      const voted = votedMap[teacher.id] === true;
      return (
        <tr key={teacher.id} data-ui-id={`${pageId}-recommend-row-${kebab(teacher.id)}`} className="text-center">
          <td className="border border-border p-2">{teacher.deptName}</td>
          <td className="border border-border p-2">{teacher.accountName}</td>
          <td className="border border-border p-2">{teacher.studentVotePercentage}</td>
          <td className="border border-border p-2">
            {readonlyMode ? (
              // teacherRowTmpl readonly 分支（recommendTeacher.jsp:96-102）：recommend→btn-voted-history。
              <span
                data-ui-id={`${pageId}-recommend-history-${kebab(teacher.id)}`}
                title={teacher.recommend === 1 ? '已推薦' : '未推薦'}
                className={`inline-block h-6 w-6 rounded-full border ${teacher.recommend === 1 ? 'border-[#ebde34cc] bg-[#ebde34cc]' : 'border-gray-400 bg-white'}`}
              />
            ) : (
              <span
                data-ui-id={`${pageId}-recommend-toggle-${kebab(teacher.id)}`}
                data-voted={voted ? 'true' : 'false'}
                className={`inline-block h-6 w-6 cursor-pointer rounded-full border ${voted ? 'border-[#ebde34cc] bg-[#ebde34cc]' : 'border-gray-400 bg-white'}`}
                onClick={() => toggle(section, type, teacher.id)}
              />
            )}
          </td>
        </tr>
      );
    });

  return (
    <ModalFrame
      uiId={`${pageId}-recommend-modal`}
      open={props.open}
      title="單位主管推薦"
      onClose={props.onClose}
      size="lg"
      footer={
        <div className="flex gap-2">
          <Button uiId={`${pageId}-recommend-close`} variant="default" onClick={props.onClose}>
            關閉
          </Button>
          {/* 檢視模式＝btn-submit hide（js:56-60）；投票模式＝show（js:41）。 */}
          {readonlyMode ? null : (
            <Button
              uiId={`${pageId}-recommend-submit`}
              variant="primary"
              dataEffect={'update:vote-dept-manager-recommend'}
              onClick={() => void submit()}
            >
              送出
            </Button>
          )}
        </div>
      }
    >
      <div className="space-y-3">
        <h4 data-ui-id={`${pageId}-recommend-info`} className="font-bold text-[#7286e5]">
          投票說明：主治醫師及住院醫師推薦，最多推薦2員
        </h4>
        {(
          [
            ['attending', '主治醫師', '優良教學主治醫師推薦名單(最多推薦2位)', attending],
            ['resident', '住院醫師', '優良教學住院醫師推薦名單(最多推薦2位)', residents],
          ] as const
        ).map(([section, type, listTitle, rows]) => (
          <div key={section}>
            <h3 data-ui-id={`${pageId}-recommend-type-${section}`} className="bg-[#e1ded5c4] p-1 text-center font-bold">
              {type}
            </h3>
            <h4 className="p-1 font-bold">{listTitle}</h4>
            <table
              data-ui-id={`${pageId}-recommend-table-${section}`}
              data-vote-name={props.voteName}
              className="w-full border-collapse text-ink"
            >
              <thead>
                <tr>
                  {MODAL_TABLE_HEAD.map((head, index) => (
                    <th key={index} className="border border-border bg-[#82d4fa14] p-2 text-center font-bold">
                      {head}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>{renderRows(section, type, rows)}</tbody>
            </table>
          </div>
        ))}
      </div>
    </ModalFrame>
  );
}

export function DeptManagerRecommendBody({ pageId }: { pageId: string }): ReactElement {
  const dataSource = useDataSource();
  const dialogs = useDialogs();
  const [filters, setFilters] = useState<Record<string, string>>({
    annualId: '-1',
    organizationId: ACCOUNT_ORGANIZATION_OPTIONS[0]?.value ?? '',
    voteStartDate: '',
    voteEndDate: '',
  });
  const [rows, setRows] = useState<readonly TableRow[]>([]);
  const [loading, setLoading] = useState(true);
  /** recommendModal 狀態（投票＝可勾選＋送出；檢視＝唯讀；js:38-47/:51-67）。 */
  const [modalState, setModalState] = useState<
    { readonly voteId: string; readonly voteName: string; readonly readonlyMode: boolean } | null
  >(null);

  /** 查詢（js:240-248 query→getVoteQueryData :250-275；mock filters 以契約 DOM name 直送，D21-4）。 */
  const query = useCallback(
    async (current: Record<string, string>) => {
      try {
        const result = await dataSource.list(pageId, {
          filters: {
            organizationId: current.organizationId ?? '',
            annualId: current.annualId === '-1' || current.annualId === undefined ? '' : current.annualId,
            voteStartDate: current.voteStartDate ?? '',
            voteEndDate: current.voteEndDate ?? '',
          },
        });
        setRows(result.rows);
      } catch (cause: unknown) {
        if (cause instanceof MockDataError) {
          await dialogs.alert(cause.message === '查詢失敗' ? cause.message : TEXT_QUERY_ERROR);
        } else {
          await dialogs.alert(TEXT_QUERY_ERROR);
        }
      }
    },
    // dataSource/dialogs 為 hooks 常數；pageId 為 props。
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pageId],
  );

  // ready 即查詢（js:18-23）。
  useEffect(() => {
    void (async () => {
      await query(filters);
      setLoading(false);
    })();
    // 初始載入一次。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** 投票/檢視（js:32-48/:51-67）：getRecommendTeacher 樣本；空清單 alert（title 提示，js:44/:63）。 */
  const openRecommend = (row: TableRow, readonlyMode: boolean): void => {
    const voteId = String(row['id']);
    const list = RECOMMEND_TEACHER_SAMPLE[voteId] ?? RECOMMEND_TEACHER_SAMPLE['default'] ?? [];
    if (list.length === 0) {
      void dialogs.alert(TEXT_NO_TEACHER_LIST, { title: '提示' });
      return;
    }
    setModalState({ voteId, voteName: String(row['name'] ?? ''), readonlyMode });
  };

  /**
   * 送出（js:136-152 的 POST 段鏡像）：update(vote row excellentTeacherRecommended)（C5-j：
   * dataEffect update:<pageId>；POST /vote/recommendTeacher/submit 未映射＝parity 登記）→
   * info(推薦成功)＋hide＋re-query（js:143-145：投票鈕消失）。
   */
  const submitRecommend = async (): Promise<void> => {
    if (modalState === null) return;
    try {
      await dataSource.update(pageId, modalState.voteId, { excellentTeacherRecommended: 1 });
      setModalState(null);
      await dialogs.alert(TEXT_SUBMIT_SUCCESS); // showInfoDialog(resp.desc)（js:143；desc＝推薦成功 :443）
      await query(filters);
    } catch {
      await dialogs.alert(TEXT_SUBMIT_ERROR); // js:151
    }
  };

  if (loading) {
    return <ProcessingOverlay uiId={`${pageId}-loading`} open />;
  }

  const columns: readonly TableColumn[] = [
    { key: 'annualYear', header: '年度', align: 'center' },
    { key: 'name', header: '投票名稱', align: 'center' },
    { key: 'voteDuration', header: '投票期間', align: 'center' },
    { key: 'id', header: '投票結果', align: 'center' },
  ];

  return (
    <div data-ui-id={pageId}>
      <LegacyContractModals pageId={pageId} />
      <ContentPanel uiId={`${pageId}-panel`} title="單位主管推薦">
        <div data-ui-id={`${pageId}-query`} className="mb-4 grid grid-cols-2 gap-x-4 gap-y-2">
          <div className="flex items-center gap-2">
            <label className="w-24 shrink-0 text-sm" htmlFor={`${pageId}-field-annual-id`}>
              <span className="text-danger">*</span>年度：
            </label>
            <Select
              uiId={`${pageId}-field-annual-id`}
              name="annualId"
              value={filters.annualId ?? ''}
              // 批次 52 種子值校準：凍結選項集 [-1=NaN,1=115]（裁定 G，NaN 鏡像）。
              options={selectOptionsOverride(pageId, 'annualId') ?? VOTE_ANNUAL_OPTIONS}
              onChange={(next) => setFilters((current) => ({ ...current, annualId: next }))}
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="w-24 shrink-0 text-sm" htmlFor={`${pageId}-field-organization-id`}>
              <span className="text-danger">*</span>醫院：
            </label>
            <Select
              uiId={`${pageId}-field-organization-id`}
              name="organizationId"
              value={filters.organizationId ?? ''}
              options={selectOptionsOverride(pageId, 'organizationId') ?? ACCOUNT_ORGANIZATION_OPTIONS}
              onChange={(next) => setFilters((current) => ({ ...current, organizationId: next }))}
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="w-24 shrink-0 text-sm">投票期間：</label>
            <TextInput
              uiId={`${pageId}-field-vote-start-date`}
              name="voteStartDate"
              value={filters.voteStartDate ?? ''}
              onChange={(next) => setFilters((current) => ({ ...current, voteStartDate: next }))}
              placeholder="yyyy/MM/dd"
            />
            <span>~</span>
            <TextInput
              uiId={`${pageId}-field-vote-end-date`}
              name="voteEndDate"
              value={filters.voteEndDate ?? ''}
              onChange={(next) => setFilters((current) => ({ ...current, voteEndDate: next }))}
              placeholder="yyyy/MM/dd"
            />
            {/* resetTime（js:99-105；D6 圓鈕以文字鈕，批次 17 同款）。 */}
            <Button
              uiId={`${pageId}-reset-time`}
              variant="default"
              size="sm"
              type="button"
              onClick={() => setFilters((current) => ({ ...current, voteStartDate: '', voteEndDate: '' }))}
            >
              清除
            </Button>
          </div>
        </div>
        <div data-ui-id={`${pageId}-actions`} className="mb-3 flex gap-2">
          <Button
            uiId={`${pageId}-query-btn`}
            variant="primary"
            size="sm"
            type="button"
            onClick={() => void query(filters)}
          >
            查詢
          </Button>
        </div>
        <Table
          uiId={`${pageId}-vote-table`}
          columns={columns}
          rows={rows}
          rowKey={(row) => `${pageId}-vote-row-${row['id']}`}
          renderCell={(row, columnKey) => {
            if (columnKey === 'id') {
              const voteId = kebabRow(String(row['id']));
              const recommended = String(row['excellentTeacherRecommended'] ?? '0') !== '0';
              return (
                <div className="flex justify-center gap-2">
                  {/* 投票鈕（js:325-326：!excellentTeacherRecommended）；檢視鈕（js:328）恆有。 */}
                  {recommended ? null : (
                    <Button
                      uiId={`${pageId}-vote-btn-${voteId}`}
                      variant="primary"
                      size="sm"
                      type="button"
                      onClick={() => openRecommend(row, false)}
                    >
                      投票
                    </Button>
                  )}
                  <Button
                    uiId={`${pageId}-view-btn-${voteId}`}
                    variant="default"
                    size="sm"
                    type="button"
                    onClick={() => openRecommend(row, true)}
                  >
                    檢視
                  </Button>
                </div>
              );
            }
            const value = row[columnKey];
            return value === null || value === undefined ? '' : String(value);
          }}
        />
      </ContentPanel>

      {/* recommendModal（recommendTeacher.jsp:107-158；送出驗證鏉在 modal 內＝legacy 直讀 DOM 等價）。 */}
      {modalState !== null ? (
        <RecommendModal
          pageId={pageId}
          open
          voteName={modalState.voteName}
          list={RECOMMEND_TEACHER_SAMPLE[modalState.voteId] ?? RECOMMEND_TEACHER_SAMPLE['default'] ?? []}
          readonlyMode={modalState.readonlyMode}
          onClose={() => setModalState(null)}
          onSubmit={(attendingVoteCnt, residentVoteCnt) => {
            void submitRecommend();
            // 勾選內容以樣本常數「送出」語意呈現（payload {excellentTeacherId,deptManagerRecommend}
            // 的逐筆寫入＝真後端批次；雛形以投票列旗標等價，D21-4）。
            void attendingVoteCnt;
            void residentVoteCnt;
          }}
        />
      ) : null}
    </div>
  );
}

/** row id → ui-id kebab 段（V001 等，kebab 保留英數）。 */
function kebabRow(value: string): string {
  return value.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase();
}
